import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const QUESTIONS = [
  { key: "interest_activity", q: "What's your favorite activity?", placeholder: "Soccer, drawing, dancing, video games..." },
  { key: "interest_hobby", q: "What's a hobby you love?", placeholder: "Reading comics, building LEGOs..." },
  { key: "interest_subject", q: "What's your favorite school subject?", placeholder: "Math, science, art, gym..." },
  { key: "interest_career", q: "What's your dream job?", placeholder: "Astronaut, chef, veterinarian..." },
  { key: "interest_explore", q: "Where would you love to explore?", placeholder: "Mars, a rainforest, deep sea..." },
];

const StudentOnboarding = () => {
  const [step, setStep] = useState(0); // 0 = anonymity notice, 1..N = questions
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Verify student is authenticated (has cookie) on mount
  useEffect(() => {
    api.get("/student/me").catch(() => navigate("/student/login", { replace: true }));
  }, [navigate]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/student/onboarding", answers);
      toast.success("You're all set! Adventure awaits!");
      navigate("/student/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const total = QUESTIONS.length;
  const isNotice = step === 0;
  const isQ = step >= 1 && step <= total;
  const qIdx = step - 1;

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col">
      <div className="container mx-auto py-6 flex justify-center">
        <Logo size={48} />
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border-2 border-mm-navy/10 rounded-2xl p-8 space-y-4" data-testid="student-onboarding">
          {isNotice && (
            <>
              <div className="w-14 h-14 rounded-full bg-mm-gold/20 text-mm-navy flex items-center justify-center mx-auto">
                <ShieldCheck size={28} strokeWidth={2.5} />
              </div>
              <h1 className="font-heading text-2xl font-black text-mm-navy text-center">Just a few fun questions first!</h1>
              <p className="text-center text-mm-navy/85 text-sm" data-testid="anonymity-notice">
                We do not collect your name or any personal information. These questions just help us make your expeditions more fun for you!
              </p>
              <button onClick={() => setStep(1)} className="btn-trail btn-gold w-full mt-4" data-testid="onboarding-start-btn">
                Let's go! 🧭
              </button>
            </>
          )}

          {isQ && (
            <>
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel text-center">
                Question {step} of {total}
              </div>
              <h2 className="font-heading text-xl font-black text-mm-navy text-center">{QUESTIONS[qIdx].q}</h2>
              <input
                value={answers[QUESTIONS[qIdx].key] || ""}
                onChange={(e) => setAnswers({ ...answers, [QUESTIONS[qIdx].key]: e.target.value })}
                placeholder={QUESTIONS[qIdx].placeholder}
                autoFocus
                className="w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
                data-testid={`onboarding-input-${qIdx}`}
              />
              {/* Progress bar */}
              <div className="h-1.5 bg-mm-navy/10 rounded-full overflow-hidden">
                <div className="h-full bg-mm-gold transition-all" style={{ width: `${(step / total) * 100}%` }} />
              </div>
              <div className="flex gap-2">
                {step > 1 && <button onClick={() => setStep(step - 1)} className="btn-trail btn-outline-navy text-sm flex-1">Back</button>}
                {step < total ? (
                  <button onClick={() => setStep(step + 1)} className="btn-trail btn-gold text-sm flex-1" data-testid={`onboarding-next-${qIdx}`}>Next</button>
                ) : (
                  <button onClick={submit} disabled={busy} className="btn-trail btn-gold text-sm flex-1 disabled:opacity-50" data-testid="onboarding-finish-btn">
                    {busy ? "Saving..." : "Start climbing!"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentOnboarding;
